<template>
  <div class="plantillas-correo">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Plantillas de Correo</h2>
        <p class="text-muted">Gestiona las plantillas de correo del sistema</p>
      </div>
      <b-button
        variant="primary"
        @click="showCreateModal = true"
        class="btn-icon"
      >
        <feather-icon icon="PlusIcon" class="mr-1" />
        Nueva Plantilla
      </b-button>
    </div>

    <!-- Filtros -->
    <b-card class="mb-4">
      <b-row>
        <b-col md="4">
          <b-form-group label="Buscar por nombre">
            <b-form-input
              v-model="filtros.nombre"
              placeholder="Nombre de la plantilla..."
              @input="filtrarPlantillas"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Estado">
            <b-form-select
              v-model="filtros.activa"
              :options="opcionesEstado"
              @change="filtrarPlantillas"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Creador">
            <b-form-select
              v-model="filtros.creador"
              :options="opcionesCreadores"
              @change="filtrarPlantillas"
            />
          </b-form-group>
        </b-col>
        <b-col md="2" class="d-flex align-items-end">
          <b-button
            variant="outline-secondary"
            @click="limpiarFiltros"
            class="w-100"
          >
            Limpiar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Lista de plantillas -->
    <b-card>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Asunto</th>
              <th>Variables</th>
              <th>Estado</th>
              <th>Creador</th>
              <th>Fecha Creación</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="plantilla in plantillasFiltradas" :key="plantilla.idPlantilla">
              <td>
                <div class="font-weight-bold">{{ plantilla.nombre }}</div>
              </td>
              <td>
                <div class="text-truncate" style="max-width: 200px;" :title="plantilla.asunto">
                  {{ plantilla.asunto }}
                </div>
              </td>
              <td>
                <b-badge variant="info">
                  {{ plantilla.variables ? plantilla.variables.length : 0 }} variables
                </b-badge>
              </td>
              <td>
                <b-badge :variant="plantilla.activa ? 'success' : 'secondary'">
                  {{ plantilla.activa ? 'Activa' : 'Inactiva' }}
                </b-badge>
              </td>
              <td>{{ plantilla.nombreCreadaPor }}</td>
              <td>{{ formatearFecha(plantilla.createdAt) }}</td>
              <td>
                <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
                  <template #button-content>
                    <feather-icon icon="MoreVerticalIcon" size="16" />
                  </template>
                  <b-dropdown-item @click="verPlantilla(plantilla)">
                    <feather-icon icon="EyeIcon" class="mr-1" />
                    Ver
                  </b-dropdown-item>
                  <b-dropdown-item @click="editarPlantilla(plantilla)">
                    <feather-icon icon="EditIcon" class="mr-1" />
                    Editar
                  </b-dropdown-item>
                  <b-dropdown-item @click="gestionarVariables(plantilla)">
                    <feather-icon icon="SettingsIcon" class="mr-1" />
                    Variables
                  </b-dropdown-item>
                  <b-dropdown-item @click="procesarPlantilla(plantilla)">
                    <feather-icon icon="SendIcon" class="mr-1" />
                    Procesar
                  </b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item 
                    @click="toggleActiva(plantilla)"
                    :class="plantilla.activa ? 'text-warning' : 'text-success'"
                  >
                    <feather-icon :icon="plantilla.activa ? 'XIcon' : 'CheckIcon'" class="mr-1" />
                    {{ plantilla.activa ? 'Desactivar' : 'Activar' }}
                  </b-dropdown-item>
                  <b-dropdown-item 
                    @click="eliminarPlantilla(plantilla)"
                    class="text-danger"
                  >
                    <feather-icon icon="TrashIcon" class="mr-1" />
                    Eliminar
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div class="text-muted">
          Mostrando {{ plantillasFiltradas.length }} de {{ plantillas.length }} plantillas
        </div>
        <b-pagination
          v-model="paginacion.paginaActual"
          :total-rows="plantillasFiltradas.length"
          :per-page="paginacion.elementosPorPagina"
          @change="cambiarPagina"
        />
      </div>
    </b-card>

    <!-- Modal Crear/Editar Plantilla -->
    <b-modal
      v-model="showCreateModal"
      :title="plantillaSeleccionada ? 'Editar Plantilla' : 'Nueva Plantilla'"
      size="lg"
      @hidden="limpiarFormulario"
    >
      <b-form @submit.prevent="guardarPlantilla">
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre" label-for="nombre">
              <b-form-input
                id="nombre"
                v-model="formulario.nombre"
                placeholder="Nombre de la plantilla"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Estado">
              <b-form-checkbox v-model="formulario.activa">
                Plantilla activa
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Asunto" label-for="asunto">
          <b-form-input
            id="asunto"
            v-model="formulario.asunto"
            :placeholder="'Asunto del correo (puede incluir variables como {{nombre_usuario}})'"
            required
          />
        </b-form-group>

        <b-form-group label="Contenido del Correo" label-for="contenido">
          <quill-editor
            ref="quillEditor"
            v-model="formulario.contenidoHtml"
            :options="editorOptions"
            @ready="onEditorReady"
            style="height: 300px;"
          />
          <b-form-text class="text-muted">
            Usa las herramientas de formato para crear el contenido del correo. Las variables como {{nombre_usuario}} se reemplazarán automáticamente.
          </b-form-text>
        </b-form-group>

        <b-form-group label="Variables Disponibles" label-for="variables">
          <b-form-textarea
            id="variables"
            v-model="formulario.variablesDisponibles"
            placeholder='["variable1", "variable2", "variable3"]'
            rows="3"
          />
          <b-form-text class="text-muted">
            Lista de variables disponibles en formato JSON
          </b-form-text>
        </b-form-group>

        <b-form-group label="Documentos Adjuntos">
          <div class="custom-file">
            <input
              type="file"
              class="custom-file-input"
              id="archivosAdjuntosPlantilla"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
              @change="onFilesSelectedPlantilla"
            />
            <label class="custom-file-label" for="archivosAdjuntosPlantilla">
              {{ formulario.archivosAdjuntos.length > 0 ? `Agregar más archivos (${formulario.archivosAdjuntos.length} ya seleccionados)` : 'Selecciona uno o más archivos para adjuntar...' }}
            </label>
          </div>
          <div class="mt-2">
            <b-button 
              variant="outline-primary" 
              size="sm" 
              @click="abrirSelectorArchivosPlantilla"
              :disabled="formulario.archivosAdjuntos.length >= 20"
            >
              <i class="fas fa-plus mr-1"></i>
              Agregar archivos
            </b-button>
            <span v-if="formulario.archivosAdjuntos.length >= 20" class="text-muted ml-2">
              <i class="fas fa-info-circle"></i>
              Máximo 20 archivos
            </span>
          </div>
          <b-form-text class="text-muted">
            <i class="fas fa-info-circle mr-1"></i>
            Formatos permitidos: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG, GIF, TXT. 
            <br>
            <strong>Máximo 10MB por archivo, 50MB total, 20 archivos máximo.</strong>
            <br>
            <em>Los archivos se almacenan tanto en base de datos como en sistema de archivos para mayor seguridad.</em>
            <br>
            <em>Puedes agregar archivos en varias tandas. Usa Ctrl/Cmd para seleccionar múltiples archivos a la vez.</em>
          </b-form-text>
        </b-form-group>

        <div v-if="formulario.archivosAdjuntos && formulario.archivosAdjuntos.length > 0" class="mt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Documentos adjuntos ({{ formulario.archivosAdjuntos.length }}):</h6>
            <b-button 
              variant="outline-secondary" 
              size="sm" 
              @click="limpiarArchivosPlantilla"
            >
              <i class="fas fa-times mr-1"></i>
              Limpiar todos
            </b-button>
          </div>
          <div class="list-group">
            <div 
              v-for="(archivo, index) in formulario.archivosAdjuntos" 
              :key="index"
              class="list-group-item d-flex justify-content-between align-items-center"
            >
              <div class="d-flex align-items-center">
                <i class="fas fa-file mr-2 text-primary"></i>
                <div>
                  <div class="font-weight-medium">{{ archivo.name }}</div>
                  <small class="text-muted">{{ formatFileSize(archivo.size) }} • {{ archivo.type || 'Archivo' }}</small>
                  <span v-if="archivo.esArchivoExistente" class="badge badge-info ml-2">
                    <i class="fas fa-database mr-1"></i>
                    Existente
                  </span>
                  <span v-if="archivo.almacenadoEnBd" class="badge badge-success ml-2">
                    <i class="fas fa-hdd mr-1"></i>
                    En BD
                  </span>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <span class="badge badge-light mr-2">#{{ index + 1 }}</span>
                <b-button 
                  variant="outline-danger" 
                  size="sm" 
                  @click="eliminarArchivoAdjunto(index)"
                  title="Eliminar archivo"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showCreateModal = false">
          Cancelar
        </b-button>
        <b-button variant="primary" @click="guardarPlantilla" :disabled="cargando">
          <span v-if="cargando" class="spinner-border spinner-border-sm mr-1"></span>
          {{ plantillaSeleccionada ? 'Actualizar' : 'Crear' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal Ver Plantilla -->
    <b-modal
      v-model="showViewModal"
      title="Vista Previa de Plantilla"
      size="lg"
    >
      <div v-if="plantillaSeleccionada">
        <h5>{{ plantillaSeleccionada.nombre }}</h5>
        <p><strong>Asunto:</strong> {{ plantillaSeleccionada.asunto }}</p>
        <div class="border rounded p-3" style="max-height: 400px; overflow-y: auto;">
          <div v-html="plantillaSeleccionada.contenidoHtml"></div>
        </div>
      </div>
    </b-modal>

    <!-- Modal Gestionar Variables -->
    <b-modal
      v-model="showVariablesModal"
      title="Gestionar Variables"
      size="lg"
    >
      <div v-if="plantillaSeleccionada">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6>Variables de "{{ plantillaSeleccionada.nombre }}"</h6>
          <b-button variant="primary" size="sm" @click="mostrarFormularioVariable">
            <feather-icon icon="PlusIcon" class="mr-1" />
            Nueva Variable
          </b-button>
        </div>

        <div v-if="variables.length === 0" class="text-center text-muted py-4">
          No hay variables definidas para esta plantilla
        </div>

        <div v-else>
          <div v-for="variable in variables" :key="variable.idVariable" class="border rounded p-3 mb-2">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="font-weight-bold">{{ variable.nombreVariable }}</div>
                <div class="text-muted small">{{ variable.descripcion }}</div>
                <div class="mt-1">
                  <b-badge :variant="variable.obligatoria ? 'danger' : 'secondary'" class="mr-1">
                    {{ variable.obligatoria ? 'Obligatoria' : 'Opcional' }}
                  </b-badge>
                  <b-badge variant="info">{{ variable.tipoVariable }}</b-badge>
                </div>
              </div>
              <div>
                <b-button variant="outline-primary" size="sm" @click="editarVariable(variable)">
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
                <b-button variant="outline-danger" size="sm" @click="eliminarVariable(variable)" class="ml-1">
                  <feather-icon icon="TrashIcon" size="14" />
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modal Procesar Plantilla -->
    <b-modal
      v-model="showProcessModal"
      title="Procesar Plantilla"
      size="xl"
    >
      <div v-if="plantillaSeleccionada">
        <h6>{{ plantillaSeleccionada.nombre }}</h6>
        <p class="text-muted">Introduce los valores para las variables de la plantilla y configura el envío</p>

        <!-- Pestañas para organizar el contenido -->
        <b-tabs content-class="mt-3">
          <!-- Pestaña Variables -->
          <b-tab title="Variables" active>
            <div v-for="variable in variables" :key="variable.idVariable" class="mb-3">
              <b-form-group :label="variable.nombreVariable">
                <b-form-input
                  v-model="valoresVariables[variable.nombreVariable]"
                  :placeholder="variable.valorPorDefecto || `Valor para ${variable.nombreVariable}`"
                  :required="variable.obligatoria"
                />
                <b-form-text class="text-muted">{{ variable.descripcion }}</b-form-text>
              </b-form-group>
            </div>
          </b-tab>

          <!-- Pestaña Destinatarios -->
          <b-tab title="Destinatarios">
            <b-form-group label="Correos electrónicos de destino">
              <b-form-textarea
                v-model="destinatariosInput"
                placeholder="Introduce los correos electrónicos separados por comas o saltos de línea&#10;Ejemplo: usuario1@empresa.com, usuario2@empresa.com"
                rows="4"
                required
              />
              <b-form-text class="text-muted">
                Separa múltiples correos con comas o saltos de línea
              </b-form-text>
            </b-form-group>

            <div v-if="destinatariosLista.length > 0" class="mt-3">
              <h6>Destinatarios validados:</h6>
              <div class="d-flex flex-wrap gap-2">
                <b-badge 
                  v-for="email in destinatariosLista" 
                  :key="email" 
                  variant="success" 
                  class="p-2"
                >
                  {{ email }}
                </b-badge>
              </div>
            </div>
          </b-tab>

          <!-- Pestaña Adjuntos -->
          <b-tab title="Adjuntos">
            <b-form-group label="Archivos adjuntos">
              <div class="custom-file">
                <input
                  type="file"
                  class="custom-file-input"
                  id="archivosAdjuntos"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
                  @change="onFilesSelected"
                />
                <label class="custom-file-label" for="archivosAdjuntos">
                  {{ archivosAdjuntos.length > 0 ? `Agregar más archivos (${archivosAdjuntos.length} ya seleccionados)` : 'Selecciona uno o más archivos para adjuntar...' }}
                </label>
              </div>
              <div class="mt-2">
                <b-button 
                  variant="outline-primary" 
                  size="sm" 
                  @click="abrirSelectorArchivos"
                  :disabled="archivosAdjuntos.length >= 20"
                >
                  <i class="fas fa-plus mr-1"></i>
                  Agregar archivos
                </b-button>
                <span v-if="archivosAdjuntos.length >= 20" class="text-muted ml-2">
                  <i class="fas fa-info-circle"></i>
                  Máximo 20 archivos
                </span>
              </div>
              <b-form-text class="text-muted">
                <i class="fas fa-info-circle mr-1"></i>
                Formatos permitidos: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG, GIF, TXT. 
                <br>
                <strong>Máximo 10MB por archivo, 50MB total, 20 archivos máximo.</strong>
                <br>
                <em>Los archivos se almacenan tanto en base de datos como en sistema de archivos para mayor seguridad.</em>
                <br>
                <em>Puedes agregar archivos en varias tandas. Usa Ctrl/Cmd para seleccionar múltiples archivos a la vez.</em>
              </b-form-text>
            </b-form-group>

            <div v-if="archivosAdjuntos && archivosAdjuntos.length > 0" class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">Archivos seleccionados ({{ archivosAdjuntos.length }}):</h6>
                <b-button 
                  variant="outline-secondary" 
                  size="sm" 
                  @click="limpiarArchivos"
                >
                  <i class="fas fa-times mr-1"></i>
                  Limpiar todos
                </b-button>
              </div>
              <div class="list-group">
                <div 
                  v-for="(archivo, index) in archivosAdjuntos" 
                  :key="index"
                  class="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div class="d-flex align-items-center">
                    <i class="fas fa-file mr-2 text-primary"></i>
                    <div>
                      <div class="font-weight-medium">{{ archivo.name }}</div>
                      <small class="text-muted">{{ formatFileSize(archivo.size) }} • {{ archivo.type || 'Archivo' }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="badge badge-light mr-2">#{{ index + 1 }}</span>
                    <b-button 
                      variant="outline-danger" 
                      size="sm" 
                      @click="eliminarArchivo(index)"
                      title="Eliminar archivo"
                    >
                      <i class="fas fa-times"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Indicador de progreso -->
            <div v-if="cargandoArchivos" class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <small class="text-muted">Enviando correo...</small>
                <small class="text-muted">{{ progresoCarga }}%</small>
              </div>
              <b-progress :value="progresoCarga" :max="100" animated></b-progress>
            </div>
          </b-tab>

          <!-- Pestaña Vista Previa -->
          <b-tab title="Vista Previa">
            <div class="border rounded p-3">
              <h6>Vista Previa del Correo</h6>
              <div class="border-bottom pb-2 mb-2">
                <strong>Asunto:</strong> {{ asuntoProcesado }}
              </div>
              <div class="border-bottom pb-2 mb-2">
                <strong>Destinatarios:</strong> {{ destinatariosLista.join(', ') }}
              </div>
              <div v-if="archivosAdjuntos && archivosAdjuntos.length > 0" class="border-bottom pb-2 mb-2">
                <strong>Adjuntos:</strong> {{ archivosAdjuntos.length }} archivo(s)
              </div>
              <div style="max-height: 300px; overflow-y: auto;">
                <div v-html="contenidoProcesado"></div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="showProcessModal = false">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="enviarCorreo" 
          :disabled="cargando || !validarFormularioEnvio"
        >
          <span v-if="cargando" class="spinner-border spinner-border-sm mr-1"></span>
          Enviar Correo
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import plantillaCorreoService from '@/services/plantillaCorreoService'
import usuarioService from '@/services/usuarioService'
import authService from '@/services/authService'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { quillEditor } from 'vue-quill-editor'
import Quill from 'quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

export default {
  name: 'PlantillasCorreo',
  components: {
    quillEditor
  },
  data() {
    return {
      plantillas: [],
      plantillasFiltradas: [],
      plantillaSeleccionada: null,
      variables: [],
      cargando: false,
      
      // Modales
      showCreateModal: false,
      showViewModal: false,
      showVariablesModal: false,
      showProcessModal: false,
      
      // Formularios
      formulario: {
        nombre: '',
        asunto: '',
        contenidoHtml: '',
        variablesDisponibles: '',
        activa: true,
        archivosAdjuntos: [],
        archivosEliminados: [] // Lista de IDs de archivos eliminados
      },
      
      valoresVariables: {},
      
      // Datos para el procesamiento de plantillas
      destinatariosInput: '',
      archivosAdjuntos: [],
      
      // Indicadores de progreso
      progresoCarga: 0,
      cargandoArchivos: false,
      
      // Filtros
      filtros: {
        nombre: '',
        activa: null,
        creador: null
      },
      
      opcionesEstado: [
        { value: null, text: 'Todos' },
        { value: true, text: 'Activas' },
        { value: false, text: 'Inactivas' }
      ],
      
      opcionesCreadores: [
        { value: null, text: 'Todos' }
      ],
      
      // Paginación
      paginacion: {
        paginaActual: 1,
        elementosPorPagina: 10
      },
      
      // Configuración del editor
      editorOptions: {
        theme: 'snow',
        placeholder: 'Escribe el contenido del correo aquí...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image']
          ]
        }
      },
      
      // Flags para evitar múltiples mensajes de advertencia
      _imagenGmailWarningShown: false,
      _imagenUrlWarningShown: false
    }
  },
  
  computed: {
    idUsuarioActual() {
      const usuarioAutenticado = authService.getCurrentUser()
      if (usuarioAutenticado) {
        return usuarioAutenticado.idUsuario || usuarioAutenticado.id
      }
      return null
    },
    
    asuntoProcesado() {
      if (!this.plantillaSeleccionada) return ''
      let asunto = this.plantillaSeleccionada.asunto
      Object.keys(this.valoresVariables).forEach(key => {
        asunto = asunto.replace(new RegExp(`{{${key}}}`, 'g'), this.valoresVariables[key] || '')
      })
      return asunto
    },
    
    contenidoProcesado() {
      if (!this.plantillaSeleccionada) return ''
      let contenido = this.plantillaSeleccionada.contenidoHtml
      Object.keys(this.valoresVariables).forEach(key => {
        contenido = contenido.replace(new RegExp(`{{${key}}}`, 'g'), this.valoresVariables[key] || '')
      })
      return contenido
    },
    
    destinatariosLista() {
      if (!this.destinatariosInput.trim()) return []
      
      // Dividir por comas y saltos de línea, limpiar espacios
      const emails = this.destinatariosInput
        .split(/[,\n]/)
        .map(email => email.trim())
        .filter(email => email.length > 0)
      
      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emails.filter(email => emailRegex.test(email))
    },
    
    validarFormularioEnvio() {
      return this.destinatariosLista.length > 0
    }
  },
  
  watch: {
    showCreateModal(newVal) {
      if (newVal) {
        // Esperar a que el modal se abra completamente y el editor esté listo
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
              const quill = this.$refs.quillEditor.quill
              // Limpiar el editor primero
              quill.setContents([])
              
              // Si estamos editando, cargar el contenido
              if (this.plantillaSeleccionada && this.plantillaSeleccionada.contenidoHtml) {
                quill.clipboard.dangerouslyPasteHTML(this.plantillaSeleccionada.contenidoHtml)
              }
            }
          }, 200)
        })
      } else {
        // Limpiar el editor cuando se cierra el modal
        this.$nextTick(() => {
          if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
            this.$refs.quillEditor.quill.setContents([])
          }
        })
      }
    }
  },
  
  async mounted() {
    await this.cargarPlantillas()
    await this.cargarCreadores()
  },
  
  methods: {
    async cargarPlantillas() {
      try {
        this.cargando = true
        this.plantillas = await plantillaCorreoService.getAllPlantillas()
        this.filtrarPlantillas()
      } catch (error) {
        this.$toast.error('Error al cargar plantillas')
        console.error(error)
      } finally {
        this.cargando = false
      }
    },
    
    async cargarCreadores() {
      try {
        const usuarios = await usuarioService.getAllUsuarios()
        
        // Mapear usuarios a opciones para el select
        const opcionesUsuarios = usuarios.map(usuario => ({
          value: usuario.idUsuario,
          text: `${usuario.nombre} ${usuario.primerApellido} ${usuario.segundoApellido || ''}`.trim()
        }))
        
        // Agregar la opción "Todos" al inicio y luego los usuarios
        this.opcionesCreadores = [
          { value: null, text: 'Todos' },
          ...opcionesUsuarios
        ]
      } catch (error) {
        console.error('Error al cargar creadores:', error)
        this.$toast.error('Error al cargar la lista de creadores')
        // Mantener al menos la opción "Todos"
        this.opcionesCreadores = [{ value: null, text: 'Todos' }]
      }
    },
    
    filtrarPlantillas() {
      let filtradas = [...this.plantillas]
      
      if (this.filtros.nombre) {
        filtradas = filtradas.filter(p => 
          p.nombre.toLowerCase().includes(this.filtros.nombre.toLowerCase())
        )
      }
      
      if (this.filtros.activa !== null) {
        filtradas = filtradas.filter(p => p.activa === this.filtros.activa)
      }
      
      if (this.filtros.creador) {
        filtradas = filtradas.filter(p => p.idCreadaPor === this.filtros.creador)
      }
      
      this.plantillasFiltradas = filtradas
      this.paginacion.paginaActual = 1
    },
    
    limpiarFiltros() {
      this.filtros = {
        nombre: '',
        activa: null,
        creador: null
      }
      this.filtrarPlantillas()
    },
    
    verPlantilla(plantilla) {
      this.plantillaSeleccionada = plantilla
      this.showViewModal = true
    },
    
    editarPlantilla(plantilla) {
      this.plantillaSeleccionada = plantilla
      this.formulario = {
        nombre: plantilla.nombre,
        asunto: plantilla.asunto,
        contenidoHtml: plantilla.contenidoHtml,
        variablesDisponibles: plantilla.variablesDisponibles,
        activa: plantilla.activa,
        archivosAdjuntos: this.convertirArchivosAdjuntos(plantilla.archivosAdjuntos || []),
        archivosEliminados: []
      }
      this.showCreateModal = true
    },
    
    async gestionarVariables(plantilla) {
      this.plantillaSeleccionada = plantilla
      try {
        this.variables = await plantillaCorreoService.getVariables(plantilla.idPlantilla)
        this.showVariablesModal = true
      } catch (error) {
        this.$toast.error('Error al cargar variables')
        console.error(error)
      }
    },
    
    async procesarPlantilla(plantilla) {
      this.plantillaSeleccionada = plantilla
      try {
        this.variables = await plantillaCorreoService.getVariables(plantilla.idPlantilla)
        this.valoresVariables = {}
        this.destinatariosInput = ''
        this.archivosAdjuntos = []
        this.showProcessModal = true
      } catch (error) {
        this.$toast.error('Error al cargar variables')
        console.error(error)
      }
    },
    
    async guardarPlantilla() {
      try {
        this.cargando = true
        
        // Separar archivos existentes de archivos nuevos
        const archivosExistentes = this.formulario.archivosAdjuntos.filter(archivo => archivo.esArchivoExistente)
        const archivosNuevos = this.formulario.archivosAdjuntos.filter(archivo => !archivo.esArchivoExistente)
        
        // Verificar si hay archivos nuevos o archivos a eliminar
        const tieneArchivosNuevos = archivosNuevos && archivosNuevos.length > 0
        const tieneArchivosAEliminar = this.formulario.archivosEliminados && this.formulario.archivosEliminados.length > 0
        const necesitaFormData = tieneArchivosNuevos || tieneArchivosAEliminar
        
        if (necesitaFormData) {
          console.log('📎 Usando FormData - Archivos nuevos:', tieneArchivosNuevos, 'Archivos a eliminar:', tieneArchivosAEliminar)
          // Usar FormData para enviar archivos
          const formData = new FormData()
          formData.append('nombre', this.formulario.nombre)
          formData.append('asunto', this.formulario.asunto)
          // Procesar el contenido HTML para convertir imágenes a base64 si es necesario
          const contenidoProcesado = await this.procesarImagenesEnHTML(this.formulario.contenidoHtml)
          formData.append('contenidoHtml', contenidoProcesado)
          formData.append('variablesDisponibles', this.formulario.variablesDisponibles)
          formData.append('activa', this.formulario.activa.toString())
          
          // Agregar solo archivos nuevos (los existentes ya están en el servidor)
          archivosNuevos.forEach((archivo, index) => {
            formData.append('archivosAdjuntos', archivo)
          })
          
          // Agregar información de archivos existentes que se mantienen
          if (archivosExistentes.length > 0) {
            const idsExistentes = archivosExistentes.map(archivo => archivo.idAdjunto)
            formData.append('archivosExistentes', idsExistentes.join(','))
          }
          
          // Agregar información de archivos eliminados
          if (this.formulario.archivosEliminados.length > 0) {
            formData.append('archivosEliminados', this.formulario.archivosEliminados.join(','))
            console.log('Enviando archivos eliminados:', this.formulario.archivosEliminados)
          }
          
          if (this.plantillaSeleccionada) {
            await plantillaCorreoService.updatePlantillaConAdjuntos(
              this.plantillaSeleccionada.idPlantilla,
              formData
            )
            this.$toast.success('Plantilla actualizada correctamente')
          } else {
            // Agregar idCreadaPor al FormData para que el backend lo reciba correctamente
            const idUsuario = this.idUsuarioActual
            if (!idUsuario) {
              this.$toast.error('No se pudo obtener el ID del usuario actual')
              return
            }
            formData.append('idCreadaPor', idUsuario.toString())
            await plantillaCorreoService.createPlantillaConAdjuntos(formData, idUsuario)
            this.$toast.success('Plantilla creada correctamente')
          }
        } else {
          console.log('📄 Usando JSON - No hay archivos nuevos ni eliminaciones')
          // Procesar el contenido HTML para convertir imágenes a base64 si es necesario
          const contenidoProcesado = await this.procesarImagenesEnHTML(this.formulario.contenidoHtml)
          const formularioConImagenes = {
            ...this.formulario,
            contenidoHtml: contenidoProcesado
          }
          
          // Envío normal sin archivos
          if (this.plantillaSeleccionada) {
            await plantillaCorreoService.updatePlantilla(
              this.plantillaSeleccionada.idPlantilla,
              formularioConImagenes
            )
            this.$toast.success('Plantilla actualizada correctamente')
          } else {
            const idUsuario = this.idUsuarioActual
            if (!idUsuario) {
              this.$toast.error('No se pudo obtener el ID del usuario actual')
              return
            }
            await plantillaCorreoService.createPlantilla(formularioConImagenes, idUsuario)
            this.$toast.success('Plantilla creada correctamente')
          }
        }
        
        // Limpiar la lista de archivos eliminados después de guardar exitosamente
        this.formulario.archivosEliminados = []
        console.log('🧹 Lista de archivos eliminados limpiada')
        
        this.showCreateModal = false
        await this.cargarPlantillas()
      } catch (error) {
        this.$toast.error('Error al guardar plantilla')
        console.error(error)
      } finally {
        this.cargando = false
      }
    },
    
    async toggleActiva(plantilla) {
      try {
        await plantillaCorreoService.toggleActiva(plantilla.idPlantilla, !plantilla.activa)
        this.$toast.success(`Plantilla ${plantilla.activa ? 'desactivada' : 'activada'} correctamente`)
        await this.cargarPlantillas()
      } catch (error) {
        this.$toast.error('Error al cambiar estado de plantilla')
        console.error(error)
      }
    },
    
    async eliminarPlantilla(plantilla) {
      if (confirm(`¿Estás seguro de que quieres eliminar la plantilla "${plantilla.nombre}"?`)) {
        try {
          await plantillaCorreoService.deletePlantilla(plantilla.idPlantilla)
          this.$toast.success('Plantilla eliminada correctamente')
          await this.cargarPlantillas()
        } catch (error) {
          this.$toast.error('Error al eliminar plantilla')
          console.error(error)
        }
      }
    },
    
    mostrarFormularioVariable() {
      // Implementar formulario para nueva variable
      this.$toast.info('Funcionalidad de nueva variable en desarrollo')
    },
    
    editarVariable(variable) {
      // Implementar edición de variable
      this.$toast.info('Funcionalidad de editar variable en desarrollo')
    },
    
    async eliminarVariable(variable) {
      if (confirm(`¿Estás seguro de que quieres eliminar la variable "${variable.nombreVariable}"?`)) {
        try {
          await plantillaCorreoService.deleteVariable(
            this.plantillaSeleccionada.idPlantilla,
            variable.idVariable
          )
          this.$toast.success('Variable eliminada correctamente')
          await this.gestionarVariables(this.plantillaSeleccionada)
        } catch (error) {
          this.$toast.error('Error al eliminar variable')
          console.error(error)
        }
      }
    },
    
    async enviarCorreo() {
      try {
        this.cargando = true
        this.cargandoArchivos = true
        this.progresoCarga = 0
        
        // Validar que hay destinatarios
        if (this.destinatariosLista.length === 0) {
          this.$toast.error('Debe especificar al menos un destinatario')
          return
        }
        
        // Validar tamaño de archivos
        const maxFileSize = 10 * 1024 * 1024 // 10MB por archivo
        const totalMaxSize = 50 * 1024 * 1024 // 50MB total
        let totalSize = 0
        
        if (this.archivosAdjuntos && this.archivosAdjuntos.length > 0) {
          for (const archivo of this.archivosAdjuntos) {
            if (archivo.size > maxFileSize) {
              this.$toast.error(`El archivo ${archivo.name} es demasiado grande. Máximo 10MB por archivo.`)
              return
            }
            totalSize += archivo.size
          }
          
          if (totalSize > totalMaxSize) {
            this.$toast.error(`El tamaño total de los archivos es demasiado grande. Máximo 50MB total.`)
            return
          }
        }
        
        // Crear FormData para enviar archivos
        const formData = new FormData()
        formData.append('idPlantilla', this.plantillaSeleccionada.idPlantilla)
        formData.append('valoresVariables', JSON.stringify(this.valoresVariables))
        formData.append('destinatarios', JSON.stringify(this.destinatariosLista))
        
        // Agregar archivos adjuntos si los hay
        if (this.archivosAdjuntos && this.archivosAdjuntos.length > 0) {
          this.archivosAdjuntos.forEach((archivo, index) => {
            formData.append('adjuntos', archivo)
          })
        }
        
        this.$toast.info('Enviando correo... Esto puede tomar unos momentos si hay archivos adjuntos.')
        
        const plantillaProcesada = await plantillaCorreoService.procesarPlantillaConAdjuntos(formData)
        this.$toast.success(`Correo enviado correctamente a ${this.destinatariosLista.length} destinatario(s)`)
        this.showProcessModal = false
      } catch (error) {
        console.error('Error al enviar correo:', error)
        
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          this.$toast.error('La operación tardó demasiado tiempo. Intenta con archivos más pequeños o menos archivos.')
        } else if (error.response && error.response.status === 413) {
          this.$toast.error('Los archivos son demasiado grandes. Reduce el tamaño de los archivos.')
        } else if (error.response && error.response.status >= 500) {
          this.$toast.error('Error del servidor. Intenta nuevamente en unos momentos.')
        } else {
          this.$toast.error('Error al enviar correo. Verifica tu conexión e intenta nuevamente.')
        }
      } finally {
        this.cargando = false
        this.cargandoArchivos = false
        this.progresoCarga = 0
      }
    },
    
    limpiarFormulario() {
      this.formulario = {
        nombre: '',
        asunto: '',
        contenidoHtml: '',
        variablesDisponibles: '',
        activa: true,
        archivosAdjuntos: [],
        archivosEliminados: []
      }
      this.plantillaSeleccionada = null
      
      // Limpiar el editor Quill
      this.$nextTick(() => {
        if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
          this.$refs.quillEditor.quill.setContents([])
        }
      })
    },
    
    cambiarPagina(pagina) {
      this.paginacion.paginaActual = pagina
    },
    
    formatearFecha(fecha) {
      return format(new Date(fecha), 'dd/MM/yyyy HH:mm', { locale: es })
    },
    
    onEditorReady(quill) {
      // Configurar el handler de imágenes para convertir a base64
      const toolbar = quill.getModule('toolbar')
      if (toolbar) {
        toolbar.addHandler('image', () => {
          this.handleImageInsert(quill)
        })
      }
      
      // Manejar cuando se pegan imágenes directamente del portapapeles (archivos)
      quill.root.addEventListener('paste', async (e) => {
        const clipboardData = e.clipboardData || window.clipboardData
        if (!clipboardData) return
        
        const items = clipboardData.items
        
        // Primero verificar si hay archivos de imagen en el portapapeles
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf('image') !== -1 && items[i].kind === 'file') {
            e.preventDefault()
            const file = items[i].getAsFile()
            this.convertImageToBase64(file, (base64) => {
              const range = quill.getSelection(true)
              quill.insertEmbed(range.index, 'image', base64)
              quill.setSelection(range.index + 1)
            })
            return
          }
        }
        
        // Si hay HTML con imágenes, procesarlo para eliminar imágenes problemáticas
        const htmlData = clipboardData.getData('text/html')
        if (htmlData && htmlData.includes('<img')) {
          // Verificar si hay imágenes de Gmail u otras URLs protegidas
          const tieneImagenesProtegidas = /<img[^>]+src\s*=\s*["'][^"']*(mail\.google\.com|gmail\.com|outlook\.com|office\.com|http:\/\/|https:\/\/)[^"']*["'][^>]*>/i.test(htmlData)
          
          if (tieneImagenesProtegidas) {
            // Procesar el HTML para eliminar solo las imágenes problemáticas
            const htmlProcesado = await this.procesarImagenesEnHTML(htmlData)
            if (htmlProcesado !== htmlData) {
              // Si se procesó el HTML, usar el procesado
              e.preventDefault()
              const range = quill.getSelection(true)
              quill.clipboard.dangerouslyPasteHTML(range.index, htmlProcesado)
              return
            }
          }
        }
        
        // Para todo lo demás (texto sin imágenes problemáticas), dejar que Quill procese normalmente
      })
      
      // El contenido se carga desde el watch de showCreateModal
      // No cargar aquí para evitar conflictos
    },
    
    // Handler para insertar imágenes como base64
    handleImageInsert(quill) {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.setAttribute('accept', 'image/*')
      input.click()
      
      input.onchange = () => {
        const file = input.files[0]
        if (file) {
          this.convertImageToBase64(file, (base64) => {
            const range = quill.getSelection(true)
            quill.insertEmbed(range.index, 'image', base64)
            quill.setSelection(range.index + 1)
          })
        }
      }
    },
    
    // Convertir imagen a base64
    convertImageToBase64(file, callback) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64 = e.target.result
        callback(base64)
      }
      reader.onerror = (error) => {
        console.error('Error al convertir imagen a base64:', error)
        this.$toast.error('Error al procesar la imagen')
      }
      reader.readAsDataURL(file)
    },
    
    // Procesar HTML para convertir todas las imágenes a base64
    async procesarImagenesEnHTML(html) {
      if (!html) return html
      
      // Buscar todas las etiquetas img
      const imgRegex = /<img[^>]+src\s*=\s*["']([^"']+)["'][^>]*>/gi
      const matches = [...html.matchAll(imgRegex)]
      
      if (matches.length === 0) return html
      
      let htmlProcesado = html
      let imagenesNoConvertidas = 0
      
      // Procesar cada imagen
      for (const match of matches) {
        const imgTag = match[0]
        const src = match[1]
        
        // Si ya es base64, no hacer nada
        if (src.startsWith('data:image/')) {
          continue
        }
        
        // Si es una URL de Gmail u otra URL protegida, eliminarla y mostrar mensaje
        if (src.includes('mail.google.com') || src.includes('gmail.com') || 
            src.includes('outlook.com') || src.includes('office.com')) {
          // Eliminar la imagen del HTML
          htmlProcesado = htmlProcesado.replace(imgTag, '')
          imagenesNoConvertidas++
          continue
        }
        
        // Si es una URL externa, intentar convertir a base64
        try {
          const base64 = await this.convertirUrlABase64(src)
          if (base64) {
            // Reemplazar el src en el HTML
            htmlProcesado = htmlProcesado.replace(imgTag, imgTag.replace(src, base64))
          } else {
            // Si no se puede convertir, eliminar la imagen
            htmlProcesado = htmlProcesado.replace(imgTag, '')
            imagenesNoConvertidas++
          }
        } catch (error) {
          console.warn('No se pudo convertir imagen a base64:', src, error)
          // Si no se puede convertir, eliminar la imagen
          htmlProcesado = htmlProcesado.replace(imgTag, '')
          imagenesNoConvertidas++
        }
      }
      
      // Mostrar mensaje si hay imágenes que no se pudieron convertir
      if (imagenesNoConvertidas > 0) {
        this.$toast.warning(`Se eliminaron ${imagenesNoConvertidas} imagen(es) con URLs externas. Por favor, súbelas usando el botón de imagen del editor.`)
      }
      
      return htmlProcesado
    },
    
    // Convertir URL de imagen a base64
    async convertirUrlABase64(url) {
      try {
        // Si es una URL externa, usar fetch
        if (url.startsWith('http://') || url.startsWith('https://')) {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error('No se pudo cargar la imagen')
          }
          const blob = await response.blob()
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        }
        // Si es una ruta local relativa, no podemos convertirla desde el navegador
        // En este caso, se mantendrá la URL original
        return null
      } catch (error) {
        console.warn('Error al convertir URL a base64:', error)
        return null
      }
    },
    
    // Métodos para manejar archivos adjuntos
    formatFileName(files) {
      if (files.length === 1) {
        return files[0].name
      } else {
        return `${files.length} archivos seleccionados`
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    eliminarArchivo(index) {
      this.archivosAdjuntos.splice(index, 1)
    },
    
    onFilesSelected(event) {
      const files = event.target.files
      console.log('Archivos seleccionados:', files)
      
      if (files && files.length > 0) {
        // Convertir FileList a Array
        const nuevosArchivos = Array.from(files)
        
        // Validar tamaño de archivos nuevos
        const maxFileSize = 10 * 1024 * 1024 // 10MB por archivo
        const totalMaxSize = 50 * 1024 * 1024 // 50MB total
        let totalSizeNuevos = 0
        
        for (const archivo of nuevosArchivos) {
          if (archivo.size > maxFileSize) {
            this.$toast.error(`El archivo ${archivo.name} es demasiado grande. Máximo 10MB por archivo.`)
            event.target.value = '' // Limpiar el input
            return
          }
          totalSizeNuevos += archivo.size
        }
        
        // Calcular tamaño total incluyendo archivos existentes
        let totalSizeExistente = 0
        for (const archivo of this.archivosAdjuntos) {
          totalSizeExistente += archivo.size
        }
        
        if (totalSizeExistente + totalSizeNuevos > totalMaxSize) {
          this.$toast.error(`El tamaño total de los archivos excedería el máximo de 50MB. Tamaño actual: ${Math.round(totalSizeExistente / 1024 / 1024)}MB, nuevos: ${Math.round(totalSizeNuevos / 1024 / 1024)}MB`)
          event.target.value = '' // Limpiar el input
          return
        }
        
        // Verificar límite de archivos
        if (this.archivosAdjuntos.length + nuevosArchivos.length > 20) {
          this.$toast.error(`No puedes agregar más de 20 archivos. Actualmente tienes ${this.archivosAdjuntos.length} archivos.`)
          event.target.value = '' // Limpiar el input
          return
        }
        
        // Agregar nuevos archivos a los existentes
        this.archivosAdjuntos = [...this.archivosAdjuntos, ...nuevosArchivos]
        this.$toast.success(`Se agregaron ${nuevosArchivos.length} archivo(s). Total: ${this.archivosAdjuntos.length} archivo(s)`)
        
        // Limpiar el input para permitir nueva selección
        event.target.value = ''
      }
    },
    
    eliminarArchivoPlantilla(index) {
      this.formulario.archivosAdjuntos.splice(index, 1)
    },
    
    limpiarArchivosPlantilla() {
      this.formulario.archivosAdjuntos = []
      this.formulario.archivosEliminados = []
      // Limpiar también el input file
      const input = document.getElementById('archivosAdjuntosPlantilla')
      if (input) {
        input.value = ''
      }
      this.$toast.info('Archivos eliminados')
    },
    
    limpiarArchivos() {
      this.archivosAdjuntos = []
      // Limpiar también el input file
      const input = document.getElementById('archivosAdjuntos')
      if (input) {
        input.value = ''
      }
      this.$toast.info('Archivos eliminados')
    },
    
    abrirSelectorArchivos() {
      const input = document.getElementById('archivosAdjuntos')
      if (input) {
        input.click()
      }
    },
    
    // Métodos para manejar archivos adjuntos en la creación de plantillas
    onFilesSelectedPlantilla(event) {
      const files = event.target.files
      console.log('Archivos seleccionados para plantilla:', files)
      
      if (files && files.length > 0) {
        // Convertir FileList a Array
        const nuevosArchivos = Array.from(files)
        
        // Validar tamaño de archivos nuevos
        const maxFileSize = 10 * 1024 * 1024 // 10MB por archivo
        const totalMaxSize = 50 * 1024 * 1024 // 50MB total
        let totalSizeNuevos = 0
        
        for (const archivo of nuevosArchivos) {
          if (archivo.size > maxFileSize) {
            this.$toast.error(`El archivo ${archivo.name} es demasiado grande. Máximo 10MB por archivo.`)
            event.target.value = '' // Limpiar el input
            return
          }
          totalSizeNuevos += archivo.size
        }
        
        // Calcular tamaño total incluyendo archivos existentes
        let totalSizeExistente = 0
        for (const archivo of this.formulario.archivosAdjuntos) {
          totalSizeExistente += archivo.size
        }
        
        if (totalSizeExistente + totalSizeNuevos > totalMaxSize) {
          this.$toast.error(`El tamaño total de los archivos excedería el máximo de 50MB. Tamaño actual: ${Math.round(totalSizeExistente / 1024 / 1024)}MB, nuevos: ${Math.round(totalSizeNuevos / 1024 / 1024)}MB`)
          event.target.value = '' // Limpiar el input
          return
        }
        
        // Verificar límite de archivos
        if (this.formulario.archivosAdjuntos.length + nuevosArchivos.length > 20) {
          this.$toast.error(`No puedes agregar más de 20 archivos. Actualmente tienes ${this.formulario.archivosAdjuntos.length} archivos.`)
          event.target.value = '' // Limpiar el input
          return
        }
        
        // Agregar nuevos archivos a los existentes
        this.formulario.archivosAdjuntos = [...this.formulario.archivosAdjuntos, ...nuevosArchivos]
        this.$toast.success(`Se agregaron ${nuevosArchivos.length} archivo(s). Total: ${this.formulario.archivosAdjuntos.length} archivo(s)`)
        
        // Limpiar el input para permitir nueva selección
        event.target.value = ''
      }
    },
    
    abrirSelectorArchivosPlantilla() {
      const input = document.getElementById('archivosAdjuntosPlantilla')
      if (input) {
        input.click()
      }
    },
    
    // Convertir archivos adjuntos del backend a formato compatible con el formulario
    convertirArchivosAdjuntos(archivosAdjuntos) {
      if (!archivosAdjuntos || archivosAdjuntos.length === 0) {
        return []
      }
      
      return archivosAdjuntos.map(adjunto => {
        // Crear un objeto similar a File pero con las propiedades del adjunto
        const archivo = {
          name: adjunto.nombreArchivo,
          size: adjunto.tamañoBytes,
          type: adjunto.tipoMime,
          lastModified: new Date(adjunto.createdAt).getTime(),
          // Propiedades adicionales para identificar archivos existentes
          idAdjunto: adjunto.idAdjunto,
          rutaArchivo: adjunto.rutaArchivo,
          esArchivoExistente: true,
          almacenadoEnBd: adjunto.almacenadoEnBd || false
        }
        return archivo
      })
    },
    
    // Eliminar archivo adjunto (tanto nuevos como existentes)
    eliminarArchivoAdjunto(index) {
      const archivo = this.formulario.archivosAdjuntos[index]
      console.log('Eliminando archivo:', archivo)
      
      // Si es un archivo existente, agregarlo a la lista de eliminados
      if (archivo.esArchivoExistente && archivo.idAdjunto) {
        this.formulario.archivosEliminados.push(archivo.idAdjunto)
        console.log('Archivo agregado a eliminados:', archivo.idAdjunto)
        console.log('Lista de eliminados:', this.formulario.archivosEliminados)
        this.$toast.info(`Archivo "${archivo.name}" será eliminado al guardar`)
      } else {
        this.$toast.info(`Archivo "${archivo.name}" eliminado`)
      }
      
      this.formulario.archivosAdjuntos.splice(index, 1)
    }
  }
}
</script>

<style scoped>
.plantillas-correo {
  padding: 1.5rem;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #5a5a5a;
}

.table td {
  vertical-align: middle;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Estilos para el editor Quill */
.ql-editor {
  min-height: 250px;
  font-family: inherit;
}

.ql-toolbar {
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-bottom: none;
}

.ql-container {
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-top: none;
}

.ql-toolbar .ql-formats {
  margin-right: 15px;
}

.ql-toolbar button {
  padding: 5px;
}

.ql-toolbar button:hover {
  color: #007bff;
}

.ql-toolbar button.ql-active {
  color: #007bff;
}
</style>
